import { Divider, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import {
  AutoComplete,
  AutoCompleteMulti,
  FormInput,
  ISelectType,
  useFormContext,
} from "@4uhub/lib4uhub";

import { ETasyScheduleTypeCode } from "../../../../../../Schedule/model";
import {
  DOMAIN_VALUE_INSURANCE_TYPE,
  DOMAIN_VALUE_PATIENT_ENCOUNTER_ORIGIN,
  DOMAIN_VALUE_PATIENT_ENCOUNTER_TYPE,
  DOMAIN_VALUE_TISS_ATTENDANCE_TYPE,
  accommodationTypeOptions,
  calendarOptions,
  domainValueOptions,
  insuranceOptions,
  patientCareCharacterOptions,
  patientEncounterClassificationOptions,
  patientEncounterComplaintOptions,
  sectorOptions,
} from "../../../../../../../services/scheduleNewCheckInOptions.service";
import { TScheduleNewCheckInForm, TThenSelectField } from "./ScheduleNewCheckInSchema";
import { useSearchableOptions } from "./useSearchableOptions";

const translationPath = "page.settings.schedule.scheduleNewCheckIn.";

// Parâmetros fixos fora do componente para manter a referência estável entre renders
const PATIENT_ENCOUNTER_TYPE_PARAMS = { Identifier: DOMAIN_VALUE_PATIENT_ENCOUNTER_TYPE };
const TISS_ATTENDANCE_TYPE_PARAMS = { Identifier: DOMAIN_VALUE_TISS_ATTENDANCE_TYPE };
const PATIENT_ENCOUNTER_ORIGIN_PARAMS = { Identifier: DOMAIN_VALUE_PATIENT_ENCOUNTER_ORIGIN };
const INSURANCE_TYPE_PARAMS = { Identifier: DOMAIN_VALUE_INSURANCE_TYPE };

const COMPLAINT_BASE_PARAMS = { enableCode: true };

type TThenSelectProps = {
  name: TThenSelectField;
  options?: ISelectType[];
  request?: (params?: any) => Promise<any>;
  params?: any;
};

const ThenSelect = ({ name, options, request, params }: TThenSelectProps) => {
  const { t } = useTranslation();

  return (
    <Grid item xs={12} sm={6}>
      <AutoComplete
        name={name}
        size="small"
        fullWidth
        options={options}
        request={request}
        params={params}
        getOptionLabel={(option) => option.name}
        label={t(translationPath + camelToSnake(name))}
      />
    </Grid>
  );
};

const camelToSnake = (value: string) =>
  value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const ScheduleNewCheckInFields = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TScheduleNewCheckInForm>();

  const scheduleType = useWatch({ control, name: "tasyScheduleType" });
  const scheduleTypeCode = scheduleType?.id;

  const scheduleTypeOptions: ISelectType[] = useMemo(
    () => [
      { id: ETasyScheduleTypeCode.CONSULTATION, name: t(translationPath + "consultation") },
      { id: ETasyScheduleTypeCode.EXAM, name: t(translationPath + "exam") },
    ],
    [t],
  );

  // As agendas dependem do tipo de agenda: sem tipo o campo fica desabilitado; com tipo a lista
  // já carrega com todas; a pesquisa filtra essa lista no navegador, sem nova chamada à API
  const calendarBaseParams = useMemo(
    () => (scheduleTypeCode ? { tasyScheduleTypeCode: scheduleTypeCode } : null),
    [scheduleTypeCode],
  );
  const calendars = useSearchableOptions(calendarOptions, calendarBaseParams);
  const complaints = useSearchableOptions(patientEncounterComplaintOptions, COMPLAINT_BASE_PARAMS);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">{t(translationPath + "section_if") || "Seção Se"}</Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={4}>
        <AutoComplete
          name="tasyScheduleType"
          size="small"
          fullWidth
          options={scheduleTypeOptions}
          getOptionLabel={(option) => option.name}
          label={t(translationPath + "tasy_schedule_type_code") || "Tipo de Agenda"}
          // Agendas de outro tipo deixam de valer ao trocar o tipo
          onValueChange={() => setValue("calendars", [])}
        />
      </Grid>

      <Grid item xs={12}>
        <AutoCompleteMulti
          name="calendars"
          size="small"
          fullWidth
          key={scheduleTypeCode ?? "no-type"}
          disable={!scheduleTypeCode}
          options={calendars.options}
          getOptionLabel={(option) => option.name}
          label={t(translationPath + "calendars") || "Agendas"}
          keepOpenOnSelect
        />
      </Grid>

      <Grid item xs={12}>
        <AutoCompleteMulti
          name="insuranceTypes"
          size="small"
          fullWidth
          request={domainValueOptions}
          params={INSURANCE_TYPE_PARAMS}
          getOptionLabel={(option) => option.name}
          label={t(translationPath + "insurance_types") || "Tipos de Convênio"}
          keepOpenOnSelect
        />
      </Grid>

      <Grid item xs={12}>
        <AutoCompleteMulti
          name="insurances"
          size="small"
          fullWidth
          request={insuranceOptions}
          getOptionLabel={(option) => option.name}
          label={t(translationPath + "insurances") || "Convênios"}
          keepOpenOnSelect
        />
      </Grid>

      <Grid item xs={12} sx={{ mt: 3 }}>
        <Typography variant="h6">{t(translationPath + "section_then") || "Seção Então"}</Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <ThenSelect name="patientEncounterType" request={domainValueOptions} params={PATIENT_ENCOUNTER_TYPE_PARAMS} />
      <ThenSelect name="complaint" options={complaints.options} />
      <ThenSelect name="tissAttendanceType" request={domainValueOptions} params={TISS_ATTENDANCE_TYPE_PARAMS} />
      <ThenSelect name="patientEncounterOrigin" request={domainValueOptions} params={PATIENT_ENCOUNTER_ORIGIN_PARAMS} />
      <ThenSelect name="patientCareCharacter" request={patientCareCharacterOptions} />
      <ThenSelect name="serviceClassification" request={patientEncounterClassificationOptions} />
      <ThenSelect name="sector" request={sectorOptions} />
      <ThenSelect name="accommodationType" request={accommodationTypeOptions} />

      <Grid item xs={12} sm={6}>
        <FormInput size="small" name="basicUnitExternalId" fullWidth label={t(translationPath + "basic_unit_id") || "ID da Unidade Básica"} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormInput size="small" name="basicUnitExternalName" fullWidth label={t(translationPath + "basic_unit_name") || "Nome da Unidade Básica"} />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormInput size="small" name="complUnitExternalId" fullWidth label={t(translationPath + "compl_unit_id") || "ID da Unidade Complementar (Opcional)"} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormInput size="small" name="complUnitExternalName" fullWidth label={t(translationPath + "compl_unit_name") || "Nome da Unidade Complementar (Opcional)"} />
      </Grid>

      <Grid item xs={12} sx={{ mt: 3 }}>
        <Typography variant="h6">{t(translationPath + "section_rules") || "Regras Finais"}</Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormInput size="small" name="metersApproximation" fullWidth type="number" label={t(translationPath + "meters_approximation") || "Aproximação em metros"} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormInput size="small" name="time" fullWidth type="number" label={t(translationPath + "time") || "Tempo Limite (min)"} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormInput size="small" name="latitude" fullWidth label={t(translationPath + "latitude") || "Latitude"} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormInput size="small" name="longitude" fullWidth label={t(translationPath + "longitude") || "Longitude"} />
      </Grid>
    </Grid>
  );
};

export default ScheduleNewCheckInFields;
